import * as Sentry from "@sentry/nextjs";
import { LRUCache } from "lru-cache";
import { makeObservable, runInAction } from "mobx";
import { nanoid } from "nanoid";

import { createLogger } from "@/utils/logger";

import type { OutcomingMessage } from "./types";
import type { SympyResult } from "@corca/sympy";

const logger = createLogger("PyodideManager");

export class PyodideManager extends EventTarget {
  private worker: Worker | null = null;

  private status: "init" | "starting" | "idle" | "executing" | "error" = "init";

  private reportedSourceCodes = new Set<string>();

  private executeCache = new LRUCache<string, string>({
    max: 100,
    ttl: 1000 * 60 * 60, // 1 hour
  });

  private executeSympyCache = new LRUCache<string, { result: SympyResult }>({
    max: 100,
    ttl: 1000 * 60 * 60, // 1 hour
  });

  constructor() {
    super();

    makeObservable<typeof this, "status" | "worker">(this, {
      worker: true,
      status: true,
      currentStatus: true,
      isInitialized: true,
      init: true,
      executeCode: true,
      executeSympyCode: true,
    });
  }

  get currentStatus() {
    return this.status;
  }

  init() {
    if (process.env.STORYBOOK) return;
    if (this.worker) return;

    this.worker = new Worker(new URL("./pyodideWorker.ts", import.meta.url));
    this.worker.onmessage = this.handleMessage.bind(this);
    this.worker.postMessage({ type: "init" });
    this.status = "starting";
  }

  get isInitialized() {
    return this.worker !== null;
  }

  get isReady() {
    return this.isInitialized && this.status !== "starting";
  }

  private handleMessage(event: MessageEvent<OutcomingMessage>) {
    const { data } = event;
    this.dispatchEvent(new CustomEvent("message", { detail: data }));

    runInAction(() => {
      switch (data.type) {
        case "init":
          this.status = "idle";
          logger.log("Pyodide initialized", data.timings);
          break;
        case "status":
          break;
        case "output":
        case "output_sympy":
          this.status = "idle";
          break;
        case "error":
          this.status = "error";
          if (!this.reportedSourceCodes.has(data.input)) {
            this.reportedSourceCodes.add(data.input);
            Sentry.captureMessage(`Pyodide error: ${data.error}`);
          }
          break;
      }
    });
  }

  public executeCode(pythonCode: string): Promise<Extract<OutcomingMessage, { type: "output" }>> {
    if (!this.isInitialized) {
      return Promise.reject(new Error("Pyodide is not initialized"));
    }

    if (this.executeCache.has(pythonCode)) {
      const detail = {
        type: "output",
        msgId: "",
        result: this.executeCache.get(pythonCode)!,
        timings: {},
        cached: true,
      } as Extract<OutcomingMessage, { type: "output" }>;
      this.dispatchEvent(new CustomEvent("message", { detail }));

      return Promise.resolve(detail);
    }

    const msgId = nanoid();
    this.worker?.postMessage({ type: "execute", msgId, code: pythonCode });

    return new Promise<Extract<OutcomingMessage, { type: "output" }>>((resolve, reject) => {
      const handleMessage = (event: CustomEventInit<OutcomingMessage>) => {
        const { detail } = event;
        if (!detail) return;

        if (detail.type === "output") {
          if (detail?.msgId !== msgId) return;
          this.removeEventListener("message", handleMessage);
          this.executeCache.set(pythonCode, detail.result);
          resolve(detail);
        } else if (detail.type === "error") {
          if (detail?.msgId !== msgId) return;
          this.removeEventListener("message", handleMessage);
          const message = `${detail.error}\n\nInput:\n${detail.input}\n\nResult:\n${detail.result}`;
          const isExpectedError = /PolynomialError/.test(detail.error);
          if (!isExpectedError && !this.reportedSourceCodes.has(detail.input)) {
            this.reportedSourceCodes.add(detail.input);
            Sentry.captureMessage(`Pyodide execution error: ${message}`);
          }
          reject(new Error(message));
        }
      };

      this.addEventListener("message", handleMessage);
    });
  }

  public executeSympyCode(
    pythonCode: string,
  ): Promise<Extract<OutcomingMessage, { type: "output_sympy" }>> {
    if (!this.isInitialized) {
      return Promise.reject(new Error("Pyodide is not initialized"));
    }

    if (this.executeSympyCache.has(pythonCode)) {
      const detail = {
        type: "output_sympy",
        msgId: "",
        result: this.executeSympyCache.get(pythonCode)!.result,
        timings: {},
        cached: true,
      } as Extract<OutcomingMessage, { type: "output_sympy" }>;
      this.dispatchEvent(new CustomEvent("message", { detail }));
      return Promise.resolve(detail);
    }

    const msgId = nanoid();
    this.worker?.postMessage({ type: "execute_sympy", msgId, code: pythonCode });

    return new Promise<Extract<OutcomingMessage, { type: "output_sympy" }>>((resolve, reject) => {
      const handleMessage = (event: CustomEventInit<OutcomingMessage>) => {
        const { detail } = event;
        if (!detail) return;

        if (detail.type === "output_sympy") {
          if (detail?.msgId !== msgId) return;
          this.removeEventListener("message", handleMessage);
          if (detail.result) {
            this.executeSympyCache.set(pythonCode, {
              result: detail.result,
            });
          }
          resolve(detail);
        } else if (detail.type === "error") {
          if (detail?.msgId !== msgId) return;
          this.removeEventListener("message", handleMessage);
          const message = `${detail.error}\n\nInput:\n${detail.input}\n\nResult:\n${detail.result}`;
          const isExpectedError = /PolynomialError/.test(detail.error);
          if (!isExpectedError && !this.reportedSourceCodes.has(detail.input)) {
            this.reportedSourceCodes.add(detail.input);
            Sentry.captureMessage(`Pyodide execution error: ${message}`);
          }
          reject(new Error(message));
        }
      };

      this.addEventListener("message", handleMessage);
    });
  }

  public dispose() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}

export const pyodideManager = new PyodideManager();
